<template>
  <vs-popup
    :title="'Edit table: ' + (table ? table.name : '')"
    @close="handlePopupClose"
    :active.sync="showThisPopup"
    class="edit-venue-table-popup"
  >
    <vs-tabs v-if="clonedTable">
      <vs-tab label="Table details">
        <h6 class="mt-6 mb-4">Setup table details</h6>
        <vs-row vs-w="12">
          <vs-input
            class="inputx mr-2"
            label="Table name"
            placeholder="Name of the table"
            v-model="clonedTable.name"
          />

          <vs-input
            class="inputx"
            label="Number of seats"
            type="number"
            v-model="clonedTable.seatCount"
            :min="1"
          />

          <vs-checkbox
            v-if="!clonedTable.isCombinationTable()"
            class="ml-2 mt-6" v-model="clonedTable.isActive"
            >Active</vs-checkbox
          >
        </vs-row>

        <vs-row vs-w="12" class="mt-6">
          <vs-checkbox class="ml-0" v-model="selectedReservedForWalkins"
            >Reserved for Walk-ins</vs-checkbox
          >
        </vs-row>

        <vs-row vs-w="12" class="mt-8">
          <vs-col vs-w="12">
            <vs-checkbox class="ml-0" v-model="clonedTable.isRoundTable"
              >Round table</vs-checkbox
            >
          </vs-col>
        </vs-row>
        <vs-row vs-w="12" class="mt-6">
          <vs-col vs-w="12">
            <label class="vs-input--label">Note (internal)</label>
            <vs-textarea
              placeholder="Add a note"
              v-model="clonedTable.note"
              height="150px"
              style="max-width: 512px"
            />
          </vs-col>
        </vs-row>
      </vs-tab>

      <vs-tab label="Policies">
        <h6 class="mt-6 mb-4">Table policies</h6>
        <edit-policies
          v-if="clonedTable"
          :object-with-policies="clonedTable"
          :policy-level="POLICY_LEVEL.Table"
        ></edit-policies>
      </vs-tab>
    </vs-tabs>

    <vs-row vs-w="12" vs-justify="flex-end">
      <vs-button
        color="warning"
        type="border"
        class="mr-4"
        @click="handleDeleteTable"
        >Delete Table</vs-button
      >
      <vs-button
        color="primary"
        :disabled="!(validForm && isDirty)"
        @click="handleSaveTableClick"
        >Save Table</vs-button
      >
    </vs-row>
  </vs-popup>
</template>

<script>
import * as _ from "lodash";
import { ACTIONS as VENUE_ACTIONS } from "@/store/modules/marcom/marcom";
import EditPolicies from "../components/policies/EditPolicies.vue";
import { POLICY_LEVEL } from "@/store/modules/marcom/data-model/policy-configurations/index.js";
import {
  SEATING_STRATEGY_TYPE,
  SeatingStrategyPreferredSeating,
} from "@/store/modules/marcom/data-model/seating-strategies/index.js";
import {
  POLICY_LIMIT_TYPE,
  POLICY_TYPE,
  TableBookingPolicy,
  TableBookingPolicyLimit,
} from "../../../../store/modules/marcom/data-model/policy-configurations";

export default {
  components: { EditPolicies },
  props: {
    setup: {
      type: Object,
    },
  },
  data() {
    return {
      POLICY_LEVEL,
      table: undefined,
      sectionId: undefined,
      clonedTable: undefined,
      showThisPopup: false,
      tablePriorities: _.union(
        [{ name: "Low (No priority)", value: null }],
        SeatingStrategyPreferredSeating.defaultValues()
      ),
    };
  },
  computed: {
    validForm() {
      return (
        !!this.clonedTable &&
        !!this.clonedTable.name &&
        !!this.clonedTable.name.trim() &&
        this.policiesValid
      );
    },
    isDirty() {
      return !_.isEqual(this.table, this.clonedTable);
    },
    selectedPriority: {
      get() {
        let preferredSeatingStrategy = this.clonedTable.seatingStrategies.find(
          (s) =>
            s.seatingStrategyType === SEATING_STRATEGY_TYPE.PreferredSeating
        );

        if (preferredSeatingStrategy) {
          let priority = this.tablePriorities.find(
            (tp) => tp.value === preferredSeatingStrategy.matchScoreModifier
          );
          if (priority) return priority;
        }

        return this.tablePriorities[0];
      },
      set(priority) {
        let indexOfpreferredSeatingStrategy = _.findIndex(
          this.clonedTable.seatingStrategies,
          (s) =>
            s.seatingStrategyType === SEATING_STRATEGY_TYPE.PreferredSeating
        );

        if (priority.value === this.tablePriorities[0].value) {
          if (indexOfpreferredSeatingStrategy > -1)
            this.clonedTable.seatingStrategies.splice(
              indexOfpreferredSeatingStrategy,
              1
            );
        } else {
          if (indexOfpreferredSeatingStrategy > -1) {
            let preferredSeatingStrategy =
              this.clonedTable.seatingStrategies[
                indexOfpreferredSeatingStrategy
              ];
            preferredSeatingStrategy.matchScoreModifier = priority.value;
            this.clonedTable.seatingStrategies.splice(
              indexOfpreferredSeatingStrategy,
              1,
              _.cloneDeep(preferredSeatingStrategy)
            );
          } else {
            let preferredSeatingStrategy = new SeatingStrategyPreferredSeating(
              priority.value,
              POLICY_LEVEL.Table
            );
            this.clonedTable.seatingStrategies.push(preferredSeatingStrategy);
          }
        }
      },
    },
    selectedReservedForWalkins: {
      get() {
        let tableBookingPolicy = this.clonedTable.venuePolicies.find(
          (p) => p.policyType === POLICY_TYPE.TableBooking
        );

        if (tableBookingPolicy && tableBookingPolicy.limits) {
          let softLimit = tableBookingPolicy.limits.find(
            (l) => l.limitType === POLICY_LIMIT_TYPE.Soft
          );
          if (softLimit && softLimit.maxPercentage === 0) return true;
        }

        return false;
      },
      set(isReservedForWalkins) {
        let tableBookingPolicy = this.clonedTable.venuePolicies.find(
          (p) => p.policyType === POLICY_TYPE.TableBooking
        );

        if (isReservedForWalkins) {
          if (!tableBookingPolicy) {
            tableBookingPolicy = new TableBookingPolicy(
              "Reserved for Walk-in policy",
              POLICY_LEVEL.Table
            );
          }

          if (!tableBookingPolicy.limits) tableBookingPolicy.limits = [];

          let softLimit = tableBookingPolicy.limits.find(
            (l) => l.limitType === POLICY_LIMIT_TYPE.Soft
          );
          if (!softLimit) {
            tableBookingPolicy.limits.push(
              new TableBookingPolicyLimit(POLICY_LIMIT_TYPE.Soft, 0, 0)
            );
          } else {
            softLimit.minPercentage = 0;
            softLimit.maxPercentage = 0;
          }

          let indexOfTableBookingPolicy = _.findIndex(
            this.clonedTable.venuePolicies,
            (p) => p.policyType === POLICY_TYPE.TableBooking
          );
          if (indexOfTableBookingPolicy >= 0)
            this.clonedTable.venuePolicies.splice(
              indexOfTableBookingPolicy,
              1,
              tableBookingPolicy
            );
          else this.clonedTable.venuePolicies.push(tableBookingPolicy);
        } else {
          if (tableBookingPolicy && tableBookingPolicy.limits) {
            let softLimitIndex = _.findIndex(
              tableBookingPolicy.limits,
              (l) => l.limitType === POLICY_LIMIT_TYPE.Soft
            );
            if (softLimitIndex >= 0) {
              tableBookingPolicy.limits.splice(softLimitIndex, 1);
            }
            if (tableBookingPolicy.limits.length === 0) {
              tableBookingPolicy.limits = null;
            }
          }
          if (tableBookingPolicy && !tableBookingPolicy.limits) {
            tableBookingPolicy = null;
          }

          let indexOfTableBookingPolicy = _.findIndex(
            this.clonedTable.venuePolicies,
            (p) => p.policyType === POLICY_TYPE.TableBooking
          );
          if (indexOfTableBookingPolicy >= 0) {
            if (tableBookingPolicy)
              this.clonedTable.venuePolicies.splice(
                indexOfTableBookingPolicy,
                1,
                tableBookingPolicy
              );
            else
              this.clonedTable.venuePolicies.splice(
                indexOfTableBookingPolicy,
                1
              );
          }
        }
      },
    },
    policiesValid() {
      return (
        !!this.clonedTable &&
        this.clonedTable.venuePolicies.every((p) => p.isValid())
      );
    },
  },
  methods: {
    show(table, section) {
      this.table = table;
      this.sectionId = section.id;

      if (!this.clonedTable) {
        this.clonedTable = _.cloneDeep(this.table);

        if (!this.clonedTable.venuePolicies) {
          this.clonedTable.venuePolicies = [];
        }

        if (!this.clonedTable.seatingStrategies) {
          this.clonedTable.seatingStrategies = [];
        }
      }

      this.showThisPopup = true;
    },
    async handleSaveTableClick() {
      try {
        this.$vs.loading();

        var tableCommand = {
          venueTable: this.clonedTable,
          venueSectionId: this.sectionId,
          venueSetupId: this.setup.id,
          note: this.note,
        };

        var result = await this.$store.dispatch(
          "marcom/" + VENUE_ACTIONS.UpdateTable,
          tableCommand
        );

        this.$vs.notify({
          color: "success",
          title: "Table saved",
          text: "Your table was saved",
        });

        this.handlePopupClose();

        this.$emit("table-saved", this.clonedTable);
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Table saved",
          text: "Oops, something went wrong",
        });
      } finally {
        this.$vs.loading.close();
      }

      this.tableName = "";
      this.numSeats = null;
    },

    async handleDeleteTable() {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Delete table",
        text: "Would you like to delete the table: " + this.table.name + "?",
        acceptText: "Delete table",
        accept: this.acceptAlertDeleteTable,
      });
    },
    async acceptAlertDeleteTable() {
      this.$vs.loading();

      let deleteTablesCommand = {
        setupId: this.setup.id,
        sectionId: this.sectionId,
        tableId: this.table.id,
      };

      try {
        var result = await this.$store.dispatch(
          "marcom/" + VENUE_ACTIONS.DeleteTable,
          deleteTablesCommand
        );

        //Update the client version
        //this.table.deleted = new Date().toString();
        this.handlePopupClose();

        this.$vs.notify({
          color: "success",
          title: "Table deleted",
          text: "Your table was deleted",
        });

        this.$emit("table-saved", this.clonedTable);
      } catch (err) {
        this.$vs.dialog({
          color: "danger",
          title: "Table was not deleted",
          text:
            "There are future bookings for this table: " +
            (err.details ? err.details : "") +
            (err.title ? err.title : "") +
            ", " +
            (err.detail ? err.detail : ""),
          acceptText: "Ok",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    handlePopupClose() {
      this.clonedTable = undefined;
      this.showThisPopup = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-venue-table-popup::v-deep .vs-popup {
  width: 1080px;

  .vs-popup--content {
    overflow: hidden;

    .vs-tabs--content {
      max-height: calc(100vh - 225px);
      overflow: auto;
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }
}
</style>
